import React, { useState, useEffect, useCallback } from "react";
import { Button, Select, Spin, Alert, Segmented } from "@multiagentbook/ui";
import {
  ArrowLeft,
  BarChart3,
  Eye,
  EyeOff,
} from "lucide-react";
import {
  Box,
  Cpu,
  Network,
  ArrowLeftRight,
  SplitSquareHorizontal,
  Combine,
  NetworkIcon,
  ChevronDown, // Import ChevronDown
} from "lucide-react";
import { LucideIcon } from "lucide-react";
import CodeViewer from "./codeviewer";
import { Implementation, Usecase, ExecutionResults } from "./types";
import AGIcon from "./icons";
import { Highlight } from "@multiagentbook/ui";
import FrameworkComparisonViz from "./FrameworkComparisonViz";
import { fetchExecutionResults } from "./utils";

// Add createSlug function (or import if moved to utils)
const createSlug = (title: string) => {
  return title
    .toLowerCase()
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/[^a-z0-9-]/g, ""); // Remove invalid characters
};

// Framework info type
type FrameworkInfo = {
  name: string;
  icon: LucideIcon;
  description?: string;
};

// Quick utility for framework info
export const getFrameworkInfo = (framework: string): FrameworkInfo => {
  const mappings: Record<string, FrameworkInfo> = {
    autogen_core: {
      name: "AutoGen Core",
      icon: AGIcon,
      description:
        "AutoGen Core is the low level API for AutoGen - a framework for building multiagent applications",
    },
    langgraph: {
      name: "LangGraph",
      icon: Network,

      description:
        "LangGraph is a framework for implementing agent control flows",
    },
    llama_index: {
      name: "LlamaIndex",
      icon: Network,
      description:
        "LlamaIndex is a framework for implementing agent control flows",
    },
    autogen_agentchat: {
      name: "AutoGen AgentChat",
      icon: AGIcon,
      description:
        "AutoGen AgentChat is a high level API for AutoGen - a framework for building multiagent applications",
    },
    google_adk: {
      name: "Google ADK",
      icon: Network,
      description:
        "An open-source AI agent framework integrated with Gemini and Google",
    },
    crewai: {
      name: "CrewAI",
      icon: Network,
      description: "CrewAI is a framework for implementing agent control flows",
    },
    openai_agents: {
      name: "OpenAI Agents",
      icon: Network,
      description:
        "The OpenAI Agents SDK enables you to build agentic AI apps in a lightweight, easy to use package",
    },
    pydanticai: {
      name: "PydanticAI",
      icon: Network,
      description:
        "PydanticAI is a Python agent framework designed to make it less painful to build production grade applications with Generative AI.",
    },
    semantic_kernel: {
      name: "Semantic Kernel Agents",
      icon: Network,
      description:
        "The Semantic Kernel Agent Framework provides a platform within the Semantic Kernel eco-system that allow for the creation of AI agents and the ability to incorporate agentic patterns into any application based on the same patterns and features that exist in the core Semantic Kernel framework.",
    },
  };
  return mappings[framework] || { name: framework, icon: Box };
};

interface UsecaseDetailProps {
  usecase: Usecase;
  onBack: () => void;
  baseUrl: string;
}

interface ComparisonFrameworks {
  left: string;
  right: string;
}

const UsecaseDetail: React.FC<UsecaseDetailProps> = ({
  usecase,
  onBack,
  baseUrl,
}) => {
  const availableFrameworks = usecase.implementations.map(
    (impl) => impl.framework
  );
  const [compareMode, setCompareMode] = useState<boolean>(false);
  const [selectedFramework, setSelectedFramework] = useState<string>(
    availableFrameworks[0]
  );
  const [comparisonFrameworks, setComparisonFrameworks] =
    useState<ComparisonFrameworks>({
      left: availableFrameworks[0],
      right: availableFrameworks[1] || availableFrameworks[0],
    });
  const [showComparisonViz, setShowComparisonViz] = useState<boolean>(false);
  const [allExecutionResults, setAllExecutionResults] = useState<
    Record<string, ExecutionResults | null>
  >({});
  const [isComparisonLoading, setIsComparisonLoading] =
    useState<boolean>(false);
  const [comparisonError, setComparisonError] = useState<string | null>(null);

  // Function to load data for the comparison viz
  const loadAllComparisonData = useCallback(async () => {
    if (Object.keys(allExecutionResults).length > 0 && !showComparisonViz) {
      // Data already loaded, just toggle visibility
      setShowComparisonViz(true);
      return;
    }
    if (isComparisonLoading) return; // Prevent concurrent fetches

    setIsComparisonLoading(true);
    setComparisonError(null);
    setShowComparisonViz(true); // Show loading state in the viz area

    try {
      // Pass the implementation object directly to the updated fetch function
      const resultsPromises = usecase.implementations.map((impl) =>
        fetchExecutionResults(baseUrl, impl) // Pass the whole implementation object
          .catch((err) => {
            console.error(
              `Failed to fetch results for ${impl.framework}:`,
              err
            );
            return null; // Return null on error for a specific framework
          })
      );
      const resultsArray = await Promise.all(resultsPromises);

      const resultsMap: Record<string, ExecutionResults | null> = {};
      usecase.implementations.forEach((impl, index) => {
        resultsMap[impl.framework] = resultsArray[index];
      });
      setAllExecutionResults(resultsMap);
    } catch (error) {
      console.error("Failed to load comparison data:", error);
      setComparisonError(
        "Failed to load some or all execution results for comparison."
      );
    } finally {
      setIsComparisonLoading(false);
    }
  }, [
    baseUrl,
    usecase.implementations, // Dependency: implementations array
    // usecase.title, // No longer directly needed if slug isn't used here
    isComparisonLoading,
    allExecutionResults,
    showComparisonViz,
    // Ensure all state values read within the callback are listed if they can change
  ]);

  const handleToggleComparisonViz = () => {
    if (showComparisonViz) {
      setShowComparisonViz(false);
    } else {
      loadAllComparisonData();
    }
  };

  const renderFrameworkControl = () => {
    if (!compareMode) {
      return (
        <div className="flex  items-center gap-4">
          <Segmented
            options={availableFrameworks.map((framework) => ({
              label: getFrameworkInfo(framework).name,
              value: framework,
            }))}
            value={selectedFramework}
            onChange={(value) => setSelectedFramework(value.toString())}
          />
          <Button
            type="primary"
            icon={<SplitSquareHorizontal className="w-4 h-4 mr-2" />}
            onClick={() => {
              setComparisonFrameworks({
                left: selectedFramework,
                right:
                  availableFrameworks.find((f) => f !== selectedFramework) ||
                  selectedFramework,
              });
              setCompareMode(true);
            }}
          >
            Compare
          </Button>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-4">
        <Select
          className="w-48"
          value={comparisonFrameworks.left}
          onChange={(value) => {
            const v = Array.isArray(value) ? value[0] : value;
            setComparisonFrameworks((prev) => ({ ...prev, left: v }));
          }}
          options={availableFrameworks.map((framework) => ({
            label: getFrameworkInfo(framework).name,
            value: framework,
            disabled: framework === comparisonFrameworks.right,
          }))}
        />
        <Button
          type="primary"
          icon={<ArrowLeftRight className="w-4 h-4" />}
          onClick={() =>
            setComparisonFrameworks((prev) => ({
              left: prev.right,
              right: prev.left,
            }))
          }
        />
        <Select
          className="w-48"
          value={comparisonFrameworks.right}
          onChange={(value) => {
            const v = Array.isArray(value) ? value[0] : value;
            setComparisonFrameworks((prev) => ({ ...prev, right: v }));
          }}
          options={availableFrameworks.map((framework) => ({
            label: getFrameworkInfo(framework).name,
            value: framework,
            disabled: framework === comparisonFrameworks.left,
          }))}
        />
        <Button
          type="primary"
          icon={<Combine className="w-4 h-4 mr-2" />}
          onClick={() => {
            setSelectedFramework(comparisonFrameworks.left);
            setCompareMode(false);
          }}
        >
          Exit Compare
        </Button>
      </div>
    );
  };

  return (
    <div className="mt-8">
      <div className="mb-6">
        <Button
          onClick={onBack}
          type="primary"
          icon={<ArrowLeft className="h-4 w-4 mr-2" />}
          className="mb-4"
        >
          Back to Usecases
        </Button>

        <h1 className="text-2xl font-bold mb-4">{usecase.title}</h1>
        <p className="text-secondary mb-4">
          {usecase.description} Implemented in{" "}
          <Highlight>
            {" "}
            {usecase.implementations.length} framework
            {usecase.implementations.length > 1 ? "s" : ""}
          </Highlight>
          .
        </p>
      </div>

      <div className="mb-4 border-b pb-4 border-dashed">
        {renderFrameworkControl()}
        {/* Compare All text with Chevron and click handler */}
        {usecase.implementations.length > 1 && (
          <div className="mt-4">
            <span
              onClick={handleToggleComparisonViz}
              className="text-blue-600 hover:text-blue-800 cursor-pointer text-sm inline-flex items-center gap-1" // Use inline-flex
              aria-expanded={showComparisonViz}
              aria-busy={isComparisonLoading && !showComparisonViz}
            >
              {isComparisonLoading && !showComparisonViz
                ? "Loading..."
                : showComparisonViz
                ? "Hide Comparison Metrics"
                : "Compare All Metrics"}
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-300 ${
                  showComparisonViz ? "rotate-180" : ""
                }`} // Add transition and conditional rotation
              />
            </span>
          </div>
        )}
      </div>

      {/* Wrapper div for smooth reveal animation */}
      <div
        className={`transition-all duration-500 ease-in-out overflow-hidden ${
          showComparisonViz ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0" // Adjust max-h as needed
        }`}
      >
        {/* Conditionally render the Comparison Visualization INSIDE the animated div */}
        {/* Keep this logic simple: if showComparisonViz is true, render content */}
        {showComparisonViz && (
          <>
            {comparisonError && (
              <Alert
                message="Error"
                description={comparisonError}
                type="error"
                showIcon
                className="mb-4"
              />
            )}
            {/* Render only when not loading OR if already loaded */}
            {(!isComparisonLoading ||
              Object.keys(allExecutionResults).length > 0) && (
              <FrameworkComparisonViz
                implementations={usecase.implementations}
                executionResults={allExecutionResults}
                // Pass loading only initially when results haven't loaded yet
                isLoading={
                  isComparisonLoading &&
                  Object.keys(allExecutionResults).length === 0
                }
              />
            )}
            {/* Show loading indicator separately if needed and results are not yet loaded */}
            {isComparisonLoading &&
              Object.keys(allExecutionResults).length === 0 && (
                <div className="text-center p-4">
                  Loading comparison data...
                </div>
              )}
          </>
        )}
      </div>

      {/* Code Viewer Grid - Add margin-top if comparison viz is shown */}
      <div
        className={`grid ${compareMode ? "grid-cols-2 gap-4" : "grid-cols-1"} ${
          showComparisonViz ? "mt-6" : "" // Add margin if viz is shown
        }`}
      >
        {compareMode ? (
          <>
            <div className="border-r border-dashed pr-4">
              <CodeViewer
                implementation={
                  usecase.implementations.find(
                    (impl) => impl.framework === comparisonFrameworks.left
                  )!
                }
                baseUrl={baseUrl}
              />
            </div>
            <div className="pl-4">
              <CodeViewer
                implementation={
                  usecase.implementations.find(
                    (impl) => impl.framework === comparisonFrameworks.right
                  )!
                }
                baseUrl={baseUrl}
              />
            </div>
          </>
        ) : (
          <CodeViewer
            implementation={
              usecase.implementations.find(
                (impl) => impl.framework === selectedFramework
              )!
            }
            baseUrl={baseUrl}
          />
        )}
      </div>
    </div>
  );
};

export default UsecaseDetail;
